import * as coam from '@cimpress-technology/coam-sapidus'
import {
  calendars,
  carrierAccounts,
  locations,
} from '@cimpress-technology/logistics-configuration-client'
import * as jsonPatch from 'fast-json-patch'
import * as _ from 'underscore'
import * as uuid from 'uuid'
import auth, { bearerToken } from '../auth'
import { createPickupCalendar, deletePickupCalendar } from './calendars-store'
import { updateLocation } from './locations-store'
import { deleteSequenceFromLocation } from './sequences-store'

export function createCarrierAccount(
  carrierAccount: carrierAccounts.models.CreateCarrierAccount
): Promise<string> {
  return carrierAccounts.createCarrierAccount(
    auth.getAccessToken(),
    uuid.v4(),
    carrierAccount
  )
}

export function deleteCarrierAccount(carrierAccountId: string) {
  return carrierAccounts.deleteCarrierAccount(
    auth.getAccessToken(),
    uuid.v4(),
    carrierAccountId
  )
}

export async function getCarrierAccountsForLocation(
  location: locations.models.Location
): Promise<Array<carrierAccounts.models.CarrierAccountWithLink | undefined>> {
  const correlationId = uuid.v4()

  try {
    return await Promise.all(
      location.carrierAccounts.map(ca =>
        carrierAccounts.getCarrierAccount(
          auth.getAccessToken(),
          correlationId,
          ca.id
        )
      )
    )
  } catch (err) {
    return []
  }
}

export function updateCarrierAccount(
  oldCarrierAccount: carrierAccounts.models.CarrierAccount,
  newCarrierAccount: carrierAccounts.models.CarrierAccount
): Promise<string> {
  return carrierAccounts.updateCarrierAccount(
    auth.getAccessToken(),
    uuid.v4(),
    oldCarrierAccount.id,
    oldCarrierAccount.etag,
    jsonPatch.compare(oldCarrierAccount, newCarrierAccount)
  )
}

export function getCarrierAccount(
  id: string
): Promise<carrierAccounts.models.CarrierAccountWithLink | undefined> {
  return carrierAccounts.getCarrierAccount(auth.getAccessToken(), uuid.v4(), id)
}

export async function createCarrierAccountForLocation(
  location: locations.models.Location,
  pickupCalendar: calendars.models.CreatePickupCalendar,
  carrierAccount: carrierAccounts.models.CreateCarrierAccount
): Promise<string> {
  const correlationId = uuid.v4()
  const accessToken = bearerToken()
  const carrierAccountId = await createCarrierAccount(carrierAccount)

  try {
    const pickupCalendarId = await createPickupCalendar(
      pickupCalendar,
      correlationId
    )

    try {
      await linkResourcesToLocation(
        location,
        pickupCalendarId,
        carrierAccountId,
        accessToken
      )

      await updateLocation(
        location.id,
        location.etag,
        [
          {
            op: 'add',
            path: '/carrierAccounts/-',
            value: { id: carrierAccountId },
          },
        ],
        correlationId
      )

      return carrierAccountId
    } catch (e) {
      await deletePickupCalendar(pickupCalendarId, correlationId)
      throw e
    }
  } catch (e) {
    await deleteCarrierAccount(carrierAccountId)
    throw e
  }
}

async function linkResourcesToLocation(
  location: locations.models.Location,
  pickupCalendarId: string,
  carrierAccountId: string,
  accessToken: string
) {
  const correlationId = uuid.v4()
  try {
    await Promise.all([
      locations.linkResourceToLocationGroup(
        accessToken,
        correlationId,
        location.id,
        pickupCalendarId,
        coam.models.ResourceTypes.Calendar
      ),
      locations.linkResourceToLocationGroup(
        accessToken,
        correlationId,
        location.id,
        carrierAccountId,
        coam.models.ResourceTypes.CarrierAccount
      ),
    ])
  } catch (e) {
    await Promise.all([
      locations.unlinkResourceFromLocationGroup(
        accessToken,
        correlationId,
        location.id,
        pickupCalendarId,
        coam.models.ResourceTypes.Calendar
      ),
      locations.unlinkResourceFromLocationGroup(
        accessToken,
        correlationId,
        location.id,
        carrierAccountId,
        coam.models.ResourceTypes.CarrierAccount
      ),
    ])
    throw e
  }
}

export async function deleteCarrierAccountForLocation(
  location: locations.models.Location,
  carrierAccountId: string
) {
  const accessToken = bearerToken()
  const correlationId = uuid.v4()

  const carrierConfigurationIndex = location.carrierAccounts.findIndex(
    ca => ca.id === carrierAccountId
  )

  await updateLocation(location.id, location.etag, [
    {
      op: 'remove',
      path: '/carrierAccounts/' + carrierConfigurationIndex,
    },
  ])

  await locations.unlinkResourceFromLocationGroup(
    accessToken,
    correlationId,
    location.id,
    carrierAccountId,
    coam.models.ResourceTypes.CarrierAccount
  )

  const carrierAccount = await getCarrierAccount(carrierAccountId)
  if (carrierAccount) {
    const sequenceIds = _.map(carrierAccount.sequences || {}, s => s.id)

    await Promise.all<Promise<void>>(
      sequenceIds.map(sequenceId =>
        deleteSequenceFromLocation(sequenceId, location.id)
      )
    )
  }

  await deleteCarrierAccount(carrierAccountId)
}
